/**
 * chat_widget/src/widget/createRootContainer.ts
 */

import type { WidgetInitOptions } from "./types/widgetTypes";
import baseCss from "./styles.css?inline";
import ReactDOM from "react-dom/client";
import { createWidgetApp } from "../bootstrap";
import { API_URL } from "../config";

export function createRootContainer(options: WidgetInitOptions) {
  const hostId = "chat-widget-host";
  const existing = document.getElementById(hostId);
  if (existing) existing.remove();

  const host = document.createElement("div");
  host.id = hostId;

  const z = options.zIndex ?? 2147483647; // max-ish
  host.style.position = "fixed";
  host.style.zIndex = String(z);

  const pos = options.position ?? "right";
  const offset = "16px";
  if (pos === "right") {
    host.style.right = offset;
    host.style.bottom = offset;
  } else {
    host.style.left = offset;
    host.style.bottom = offset;
  }

  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });

  // Style isolé (pas impacté par le CSS du site)
  const styleTag = document.createElement("style");
  const accent = options.accentColor ?? "#3b82f6";
  // Provide CSS variable for theming
  styleTag.textContent = `:host{--accent-color:${accent};}` + baseCss;
  shadow.appendChild(styleTag);

  // Root div dans le shadow
  const rootEl = document.createElement("div");
  rootEl.setAttribute("data-chat-root", "1");
  shadow.appendChild(rootEl);

  let reactRoot: any = null;
  let isMounted = false;

  function mount() {
    if (isMounted) return;
    isMounted = true;

    // Static imports (single-file IIFE build). Avoids init-order issues in minified bundles.
    reactRoot = ReactDOM.createRoot(rootEl);
    reactRoot.render(createWidgetApp(options, shadow));
  }

  // --- Tenant validation (hide widget if invalid) ---
  const tenantAbort = new AbortController();
  let destroyed = false;

  async function isUserValid(userId: string): Promise<boolean> {
    // Fail-closed: if we can't validate, we don't show the widget
    try {
      const timeoutId = window.setTimeout(() => tenantAbort.abort(), 5000);
      const res = await fetch(
        `${API_URL}/users?apiTokenValue=${encodeURIComponent(userId)}&$select[]=chatSettings&$select[]=services&$select[]=photos`,
        {
          method: "GET",
          signal: tenantAbort.signal,
          headers: { caller: "system" },
        },
      );

      window.clearTimeout(timeoutId);
      const user = await res.json();
      const resolvedUserId =
        user &&
        user.data &&
        Array.isArray(user.data) &&
        user.data[0] &&
        user.data[0]._id
          ? String(user.data[0]._id)
          : undefined;

      // Persist userId for the lifetime of this widget instance
      if (resolvedUserId) {
        options.userId = resolvedUserId;
        options.actions = user.data[0].chatSettings.actions;
        options.services = user.data[0].services;
        options.lang = user.data[0].chatSettings.lang;
        options.avatar = user.data?.[0]?.photos?.[0]?.src || null
      } else {
        return false;
      }
      return res.ok;
    } catch {
      return false;
    }
  }

  // Validate tenant before mounting anything
  isUserValid(options.userId).then((valid) => {
    if (!valid || destroyed) return;

    // Mount the React app immediately after validation.
    // The React UI should handle launcher + badge + open/close state.
    mount();
  });

  function destroy() {
    destroyed = true;
    try {
      tenantAbort.abort();
    } catch {
      //
    }

    try {
      if (reactRoot) reactRoot.unmount?.();
    } catch {
      //
    }
    host.remove();
  }

  return { destroy };
}
